/**
 * The answer key of a word grid: its groups, easiest first, each with its label. Kept apart
 * from the component so the eagerly loaded module file never pulls the form into the bundle.
 */
import { foldWord } from "./groups";
import type { WordGroup, WordGroupsDefinition, WordGroupsSolution } from "./types";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function emptySolution(definition: WordGroupsDefinition): WordGroupsSolution {
  return {
    groups: Array.from({ length: definition.groupCount }, () => ({
      label: "",
      words: Array.from({ length: definition.groupSize }, () => ""),
    })),
  };
}

/** Reads whatever is stored, however wrong, and keeps what it can use. */
export function solutionOf(value: unknown): WordGroupsSolution {
  if (!isRecord(value) || !Array.isArray(value.groups)) return { groups: [] };
  return {
    groups: value.groups.filter(isRecord).map((group) => ({
      label: typeof group.label === "string" ? group.label : "",
      words: Array.isArray(group.words)
        ? group.words.filter((w): w is string => typeof w === "string")
        : [],
    })),
  };
}

/** The key reshaped to the definition's grid: missing groups and slots added, extras kept. */
export function fitSolution(
  definition: WordGroupsDefinition,
  solution: WordGroupsSolution,
): WordGroupsSolution {
  const groups: WordGroup[] = [];
  const count = Math.max(definition.groupCount, solution.groups.length);
  for (let g = 0; g < count; g++) {
    const group = solution.groups[g] ?? { label: "", words: [] };
    const words = [...group.words];
    while (words.length < definition.groupSize) words.push("");
    groups.push({ label: group.label, words });
  }
  return { groups };
}

export interface KeyReport {
  /** Words of the definition that no group holds. */
  unplaced: string[];
  /** Words typed in the key that the definition does not list. */
  unknown: string[];
  /** Words placed in two groups. */
  doubled: string[];
}

/** What the server's `validate_solution` would object to, for the author's eye. */
export function keyReport(
  definition: WordGroupsDefinition,
  solution: WordGroupsSolution,
): KeyReport {
  const listed = new Map(
    definition.words.filter((w) => w.trim()).map((word) => [foldWord(word), word] as const),
  );
  const placed = new Set<string>();
  const report: KeyReport = { unplaced: [], unknown: [], doubled: [] };
  for (const group of solution.groups) {
    for (const word of group.words) {
      const key = foldWord(word);
      if (!key) continue;
      if (!listed.has(key)) report.unknown.push(word.trim());
      else if (placed.has(key)) report.doubled.push(word.trim());
      placed.add(key);
    }
  }
  for (const [key, word] of listed) if (!placed.has(key)) report.unplaced.push(word);
  return report;
}
